"use strict";
import { useMemo } from "react";
import { useSelector } from "react-redux";
import { MAX_RECENT_SEARCH_RESULTS } from "components/TokenSelector/constants";
import { useCurrencyInfosToTokenOptions } from "components/TokenSelector/hooks/useCurrencyInfosToTokenOptions";
import { usePoolSearchResultsToPoolOptions } from "components/lists/items/pools/usePoolSearchResultsToPoolOptions";
import {
  OnchainItemListOptionType
} from "components/lists/items/types";
import { getNativeAddress } from "constants/uniswapAddresses";
import { SearchTab } from "features/search/SearchModal/types";
import {
  isEtherscanSearchResult,
  isNFTCollectionSearchResult,
  isPoolSearchResult,
  isTokenSearchResult,
  isWalletSearchResult
} from "features/search/SearchResult";
import { selectSearchHistory } from "features/search/selectSearchHistory";
import { useCurrencyInfos } from "features/tokens/useCurrencyInfo";
import { buildCurrencyId, buildNativeCurrencyId, currencyId } from "utils/uniswapCurrencyId";
import { isMobileApp } from "utilities/src/platform";
export function useRecentlySearchedOptions({
  chainFilter,
  activeTab,
  numberOfRecentSearchResults = MAX_RECENT_SEARCH_RESULTS
}) {
  const recentHistory = useSelector(selectSearchHistory).filter((searchResult) => {
    switch (activeTab) {
      case SearchTab.Tokens:
        return isTokenSearchResult(searchResult);
      case SearchTab.Pools:
        return isPoolSearchResult(searchResult);
      case SearchTab.Wallets:
        return isWalletSearchResult(searchResult);
      case SearchTab.NFTCollections:
        return isNFTCollectionSearchResult(searchResult);
      default:
      case SearchTab.All:
        return isMobileApp ? isTokenSearchResult(searchResult) || isWalletSearchResult(searchResult) || isNFTCollectionSearchResult(searchResult) : isTokenSearchResult(searchResult) || isPoolSearchResult(searchResult);
    }
  }).filter((searchResult) => {
    return isWalletSearchResult(searchResult) || isEtherscanSearchResult(searchResult) || (chainFilter ? searchResult.chainId === chainFilter : true);
  }).slice(0, numberOfRecentSearchResults);
  const currencyIds = recentHistory.filter(isTokenSearchResult).map((searchResult) => {
    const id = searchResult.address ? buildCurrencyId(searchResult.chainId, searchResult.address) : buildNativeCurrencyId(searchResult.chainId);
    return id;
  });
  const tokenCurrencyInfos = useCurrencyInfos(currencyIds);
  const tokenOptions = useCurrencyInfosToTokenOptions({
    currencyInfos: tokenCurrencyInfos.filter((info) => info !== void 0)
  });
  const poolOptions = usePoolSearchResultsToPoolOptions(recentHistory.filter(isPoolSearchResult));
  const walletOptions = recentHistory.filter((result) => isWalletSearchResult(result)).map(
    (searchResult) => ({
      ...searchResult,
      type: OnchainItemListOptionType.WalletByAddress
    })
  );
  const nftOptions = recentHistory.filter(isNFTCollectionSearchResult).map((searchResult) => ({
    ...searchResult,
    type: OnchainItemListOptionType.NFTCollection
  }));
  return useMemo(() => {
    if (recentHistory.every(isTokenSearchResult)) {
      return tokenOptions ?? [];
    } else if (recentHistory.every(isPoolSearchResult)) {
      return poolOptions;
    } else if (recentHistory.every(isWalletSearchResult)) {
      return walletOptions;
    } else if (recentHistory.every(isNFTCollectionSearchResult)) {
      return nftOptions;
    }
    const tokenOptionsMap = {};
    tokenOptions?.forEach((option) => {
      tokenOptionsMap[currencyId(option.currencyInfo.currency).toLowerCase()] = option;
    });
    const poolOptionsMap = {};
    poolOptions.forEach((option) => {
      poolOptionsMap[`${option.chainId}-${option.poolId.toLowerCase()}`] = option;
    });
    const walletOptionsMap = {};
    walletOptions.forEach((option) => {
      walletOptionsMap[option.address.toLowerCase()] = option;
    });
    const nftOptionsMap = {};
    nftOptions.forEach((option) => {
      nftOptionsMap[option.address.toLowerCase()] = option;
    });
    const data = [];
    recentHistory.forEach((asset) => {
      if (isTokenSearchResult(asset)) {
        const option = tokenOptionsMap[buildCurrencyId(asset.chainId, asset.address ?? getNativeAddress(asset.chainId)).toLowerCase()];
        option && data.push(option);
      } else if (isPoolSearchResult(asset)) {
        const option = poolOptionsMap[`${asset.chainId}-${asset.poolId.toLowerCase()}`];
        option && data.push(option);
      } else if (isWalletSearchResult(asset)) {
        const option = walletOptionsMap[asset.address.toLowerCase()];
        option && data.push(option);
      } else if (isNFTCollectionSearchResult(asset)) {
        const option = nftOptionsMap[asset.address.toLowerCase()];
        option && data.push(option);
      }
    });
    return data;
  }, [recentHistory, tokenOptions, poolOptions, walletOptions, nftOptions]);
}
